import React, { Fragment, useEffect } from "react";
import { Popover, Transition } from "@headlessui/react";
import { Chip } from "@scripts/react/components/Chip";
// @ts-ignore - React version mismatch between packages
import { ProgressBar } from "@scripts/react/components/Models/ModelTrainMetricsTd";
import { useCreditBannerStore } from "@/store/slices/creditBannerSlice";
import { useFlowState } from "@/hooks/useFlowState";
import { BuildSteps, Flows } from "@/types/flows";
import { useCurrentWorkspace } from "@/store";
import { CREDIT_PROMO_LEARN_MORE_URL } from "./constants";
import { useMobile } from "@/hooks/useMobile";

export const CreditChip = () => {
    const { isPromoEnabled, isBannerDismissed, promoStatus, fetchPromoStatus } =
        useCreditBannerStore();
    const { flow, step } = useFlowState();
    const currentWorkspace = useCurrentWorkspace();
    const isMobile = useMobile();

    // Fetch promo status on mount and when workspace changes
    useEffect(() => {
        fetchPromoStatus(currentWorkspace?.id);
    }, [currentWorkspace?.id, fetchPromoStatus]);

    // Hide chip if promo is disabled or not active
    if (!isPromoEnabled || (promoStatus && !promoStatus.isActive)) {
        return null;
    }

    // Hide chip on homepage (BUILD flow, UPLOAD step) unless banner is dismissed
    // Always show on mobile since banner is hidden on mobile
    const isHomepage = flow === Flows.BUILD && step === BuildSteps.UPLOAD;
    if (isHomepage && !isBannerDismissed && !isMobile) {
        return null;
    }

    // Calculate display values from promo status
    const totalValueDollars = promoStatus?.totalValueDollars ?? 6000;
    const creditLimit = promoStatus?.creditLimit ?? 2000;
    const percentUsed = promoStatus?.percentUsed ?? 0;
    const endDate = promoStatus?.promotion?.endDate
        ? new Date(promoStatus.promotion.endDate).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric"
          })
        : "December 31, 2025";

    // Format dollar value (e.g., 6000 -> "$6,000")
    const formattedTotalValue = `$${totalValueDollars.toLocaleString()}`;
    const formattedCreditLimit = creditLimit.toLocaleString();

    return (
        <Popover className="relative">
            <Popover.Button as="div" className="cursor-pointer focus:outline-none">
                <Chip
                    color="white"
                    className="cursor-pointer !border !border-yellow-500 !text-yellow-700 transition-shadow hover:bg-yellow-50 hover:shadow-lg hover:shadow-yellow-500/10"
                >
                    <i className="far fa-gift"></i>
                    {formattedTotalValue} Credit
                </Chip>
            </Popover.Button>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Popover.Panel className="absolute right-0 z-[9999] mt-2 flex w-72 flex-col gap-5 rounded-lg bg-white p-3 shadow-lg shadow-yellow-500/10 ring-1 ring-yellow-500">
                    <p className="font-nomral text-sm text-primary">
                        Kick-start your Rapid model with {formattedCreditLimit} credits on us —
                        worth {formattedTotalValue}! <br></br>
                        <br></br>Until {endDate}
                        {CREDIT_PROMO_LEARN_MORE_URL && (
                            <>
                                {" "}
                                <a
                                    href={CREDIT_PROMO_LEARN_MORE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-secondary underline"
                                >
                                    Learn More
                                </a>
                            </>
                        )}
                    </p>
                    <div className="flex w-full items-center gap-2">
                        <ProgressBar
                            value={percentUsed / 100}
                            colorScheme="yellow"
                            progressBarClassName="!h-3"
                        />
                        <span className="flex-shrink-0 text-sm text-tertiary">
                            {Math.round(percentUsed)}% Used
                        </span>
                    </div>
                </Popover.Panel>
            </Transition>
        </Popover>
    );
};
